import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BarChart } from '@mui/x-charts/BarChart';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import Group from '@/models/Organization/Group';
import { SubscriptionTier } from '../../../../generated-client/models';
import { getTierChipSx } from '@/utils/entitlements/tierChipStyles';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import {
  buildSubscriptionBreakdownByGroups,
  formatSubscriptionBarValue,
  formatSubscriptionListCount,
  getSubscriptionBreakdownCategoryGapRatio,
  getSubscriptionBreakdownOverlayWidthPx,
  getSubscriptionBreakdownStepPx,
  getSubscriptionBreakdownTrackWidthPx,
  SUBSCRIPTION_BREAKDOWN_BAR_GAP_RATIO,
  SUBSCRIPTION_BREAKDOWN_DIALOG_PADDING_X_PX,
  SUBSCRIPTION_BREAKDOWN_MIN_BAR_SIZE_PX,
  SUBSCRIPTION_BREAKDOWN_VISIBLE_GROUPS,
  toSubscriptionBarHeight,
  type GroupSubscriptionCounts,
} from '@/utils/organization/allUsersOverview';

export type SubscriptionPoolCounts = {
  purchased: number;
  used: number;
  remaining: number;
};

export type SubscriptionPoolSummary = {
  ops: SubscriptionPoolCounts;
  respond: SubscriptionPoolCounts;
};

export type SubscriptionBreakdownOverlayProps = {
  open: boolean;
  onClose: () => void;
  allGroups: Group[];
  companyName: string;
  poolSummary: SubscriptionPoolSummary;
};

const TIER_BAR_COLORS = {
  connect: COLORS.CONNECT_TEXT_GRAY,
  ops: COLORS.SAFEGUARD_BLUE,
  respond: COLORS.DARK_GREEN,
} as const;

const CHART_HEIGHT = 380;
const CHART_BAR_TOP_RADIUS = 2;
const CHART_Y_AXIS_WIDTH = 64;
const CHART_X_AXIS_HEIGHT = 36;
const CHART_X_AXIS_TICK_SIZE_PX = 10;
const CHART_MARGIN = { left: 0, right: 8, top: 8, bottom: 8 };
const CHART_HORIZONTAL_MARGIN =
  CHART_MARGIN.left + CHART_MARGIN.right;
const CHART_VERTICAL_MARGIN = {
  left: 0,
  right: 0,
  top: CHART_MARGIN.top,
  bottom: CHART_MARGIN.bottom,
};
const AXIS_LABEL_FONT_SIZE = 16;
const AXIS_LABEL_STYLE = {
  fill: COLORS.SAFEGUARD_BLUE,
  fontSize: AXIS_LABEL_FONT_SIZE,
  fontWeight: 500,
};
const CHART_AXIS_SX = {
  '& .MuiChartsAxis-line': { display: 'none' },
  '& .MuiChartsAxis-tick': { display: 'none' },
};
const GROUPS_SCROLL_BUTTON_SIZE_PX = 28;
const CHART_LEGEND_SWATCH_WIDTH_PX = 12;
const CHART_LEGEND_SWATCH_HEIGHT_PX = 10;
const SCROLL_EDGE_PX = 1;
const CHART_LEGEND_ITEMS = [
  {
    id: 'connect',
    label: getTierLabelKey(SubscriptionTier.Connect),
    color: TIER_BAR_COLORS.connect,
  },
  {
    id: 'ops',
    label: getTierLabelKey(SubscriptionTier.Ops),
    color: TIER_BAR_COLORS.ops,
  },
  {
    id: 'respond',
    label: getTierLabelKey(SubscriptionTier.Respond),
    color: TIER_BAR_COLORS.respond,
  },
] as const;
const PLOT_BACKGROUND = COLORS.TABLE_HEADER_GRAY;
const LIST_GRID_COLUMNS = 'minmax(0, 1.4fr) repeat(3, minmax(0, 1fr))';
const LIST_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: LIST_GRID_COLUMNS,
  columnGap: { xs: 0.75, md: 2 },
  alignItems: 'center',
} as const;
const LIST_HEADER_STICKY_SX = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: PLOT_BACKGROUND,
} as const;

type BreakdownViewMode = 'chart' | 'list';

const getGroupsScrollState = (
  scroller: HTMLDivElement | null,
  groupCount: number,
) => {
  if (!scroller || scroller.clientWidth === 0) {
    return {
      canScrollLeft: false,
      canScrollRight: groupCount > SUBSCRIPTION_BREAKDOWN_VISIBLE_GROUPS,
    };
  }
  return {
    canScrollLeft: scroller.scrollLeft > SCROLL_EDGE_PX,
    canScrollRight:
      scroller.scrollLeft <
      scroller.scrollWidth - scroller.clientWidth - SCROLL_EDGE_PX,
  };
};

const SubscriptionBreakdownOverlay = ({
  open,
  onClose,
  allGroups,
  companyName,
  poolSummary,
}: SubscriptionBreakdownOverlayProps) => {
  const { t } = useTranslation();
  const rows = useMemo(
    () => buildSubscriptionBreakdownByGroups(allGroups),
    [allGroups],
  );
  const dataset = useMemo(
    () =>
      rows.map((row) => ({
        group: row.groupName,
        connect: toSubscriptionBarHeight(row.connect),
        ops: toSubscriptionBarHeight(row.ops),
        respond: toSubscriptionBarHeight(row.respond),
      })),
    [rows],
  );
  const yMax = Math.max(
    1,
    ...rows.flatMap((row) => [row.connect, row.ops, row.respond]),
  );
  const trackWidthPx = getSubscriptionBreakdownTrackWidthPx(
    dataset.length,
    0,
    CHART_HORIZONTAL_MARGIN,
  );
  const overlayWidthPx = getSubscriptionBreakdownOverlayWidthPx(
    CHART_Y_AXIS_WIDTH,
    CHART_HORIZONTAL_MARGIN,
  );
  const groupStepPx = getSubscriptionBreakdownStepPx();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(
    () => dataset.length > SUBSCRIPTION_BREAKDOWN_VISIBLE_GROUPS,
  );
  const [viewMode, setViewMode] = useState<BreakdownViewMode>('chart');

  // Resetting on click (instead of after exit) would swap the content
  // while the dialog is still fading out.
  const resetViewModeAfterClose = () => {
    setViewMode('chart');
  };

  const updateGroupsScrollState = useCallback(() => {
    const next = getGroupsScrollState(scrollRef.current, dataset.length);
    setCanScrollLeft(next.canScrollLeft);
    setCanScrollRight(next.canScrollRight);
  }, [dataset.length]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updateGroupsScrollState();
  }, [open, trackWidthPx, overlayWidthPx, updateGroupsScrollState]);

  const scrollGroupsBy = (direction: -1 | 1) => {
    scrollRef.current?.scrollBy({
      left: direction * groupStepPx,
      behavior: 'smooth',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="subscription-breakdown-title"
      slotProps={{
        transition: { onExited: resetViewModeAfterClose },
        paper: {
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            width: overlayWidthPx,
            maxWidth: { xs: 'calc(100% - 24px)', sm: '100%' },
            m: { xs: 1.5, sm: 4 },
          },
          ...({ 'data-overlay-width': overlayWidthPx } as Record<
            string,
            unknown
          >),
        },
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 2, sm: `${SUBSCRIPTION_BREAKDOWN_DIALOG_PADDING_X_PX}px` },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="subscription-breakdown-title"
              sx={{
                color: COLORS.SAFEGUARD_BLUE,
                fontSize: { xs: 18, sm: 20 },
                fontWeight: 500,
                lineHeight: 1.2,
                pr: 1,
              }}
            >
              {t(StringKeys.SUBSCRIPTIONS_BREAKDOWN_BY_GROUPS)}
            </Typography>
            <Typography
              sx={{
                color: COLORS.GREY,
                fontSize: 13,
                fontWeight: 500,
                mt: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {companyName}
            </Typography>
          </Box>
          <IconButton aria-label={t(StringKeys.CLOSE)} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          data-testid="subscription-breakdown-pool-summary"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: { xs: 1, sm: 1.5 },
          }}
        >
          <PoolSummaryRow
            variant="ops"
            label={getTierLabelKey(SubscriptionTier.Ops)}
            counts={poolSummary.ops}
          />
          <PoolSummaryRow
            variant="respond"
            label={getTierLabelKey(SubscriptionTier.Respond)}
            counts={poolSummary.respond}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_event, next: BreakdownViewMode | null) => {
              if (next) {
                setViewMode(next);
              }
            }}
            aria-label={t(StringKeys.BREAKDOWN_VIEW)}
            sx={{
              backgroundColor: COLORS.TABLE_HEADER_GRAY,
              borderRadius: 3,
              p: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                mx: 0.25,
                borderRadius: '8px !important',
                textTransform: 'none',
                px: 1,
                py: 0,
                fontSize: 13,
                fontWeight: 500,
                color: COLORS.GREY,
                '&.Mui-selected': {
                  backgroundColor: COLORS.WHITE,
                  color: COLORS.SAFEGUARD_BLUE,
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: COLORS.WHITE,
                },
              },
            }}
          >
            <ToggleButton value="chart">{t(StringKeys.VIEW_AS_CHART)}</ToggleButton>
            <ToggleButton value="list">{t(StringKeys.VIEW_AS_LIST)}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === 'list' ? (
          <OccupancyList rows={rows} />
        ) : (
        <Box
          data-testid="subscription-breakdown-plot"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            borderRadius: 2,
            backgroundColor: PLOT_BACKGROUND,
            px: 4,
           pt:2,
            pb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              width: '100%',
              minWidth: 0,
            }}
          >
            <Box
              data-testid="subscription-breakdown-y-axis"
              sx={{
                flexShrink: 0,
                width: CHART_Y_AXIS_WIDTH,
                bgcolor: 'transparent',
                zIndex: 1,
              }}
            >
              <BarChart
                height={CHART_HEIGHT}
                width={CHART_Y_AXIS_WIDTH}
                hideLegend
                margin={CHART_VERTICAL_MARGIN}
                sx={{
                  ...CHART_AXIS_SX,
                  '& .MuiBarElement-root': { display: 'none' },
                  '& .MuiChartsAxis-directionX': { display: 'none' },
                }}
                xAxis={[
                  {
                    data: [''],
                    scaleType: 'band',
                    height: CHART_X_AXIS_HEIGHT,
                    disableLine: true,
                    disableTicks: true,
                  },
                ]}
                yAxis={[
                  {
                    min: 0,
                    max: yMax,
                    label: t(StringKeys.NUMBER_OF_SUBSCRIPTIONS_USED),
                    disableLine: true,
                    disableTicks: true,
                    width: CHART_Y_AXIS_WIDTH,
                    labelStyle: AXIS_LABEL_STYLE,
                  },
                ]}
                series={[{ data: [0], color: 'transparent' }]}
              />
            </Box>
            <Box
              ref={scrollRef}
              data-testid="subscription-breakdown-scroll"
              data-track-width={trackWidthPx}
              tabIndex={0}
              aria-label={t(StringKeys.SUBSCRIPTION_GROUPS_SCROLL)}
              onScroll={updateGroupsScrollState}
              sx={{
                overflowX: 'auto',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                data-testid="subscription-breakdown-track"
                sx={{ width: trackWidthPx }}
              >
                <BarChart
                  dataset={dataset}
                  width={trackWidthPx}
                  height={CHART_HEIGHT}
                  hideLegend
                  borderRadius={CHART_BAR_TOP_RADIUS}
                  margin={CHART_MARGIN}
                  sx={CHART_AXIS_SX}
                  xAxis={[
                    {
                      scaleType: 'band',
                      dataKey: 'group',
                      categoryGapRatio: getSubscriptionBreakdownCategoryGapRatio(),
                      barGapRatio: SUBSCRIPTION_BREAKDOWN_BAR_GAP_RATIO,
                      disableLine: true,
                      disableTicks: false,
                      tickSize: CHART_X_AXIS_TICK_SIZE_PX,
                      height: CHART_X_AXIS_HEIGHT,
                    },
                  ]}
                  yAxis={[
                    {
                      min: 0,
                      max: yMax,
                      position: 'none',
                      disableLine: true,
                      disableTicks: true,
                    },
                  ]}
                  series={[
                    {
                      dataKey: 'connect',
                      label: getTierLabelKey(SubscriptionTier.Connect),
                      color: TIER_BAR_COLORS.connect,
                      minBarSize: SUBSCRIPTION_BREAKDOWN_MIN_BAR_SIZE_PX,
                      valueFormatter: formatSubscriptionBarValue,
                    },
                    {
                      dataKey: 'ops',
                      label: getTierLabelKey(SubscriptionTier.Ops),
                      color: TIER_BAR_COLORS.ops,
                      minBarSize: SUBSCRIPTION_BREAKDOWN_MIN_BAR_SIZE_PX,
                      valueFormatter: formatSubscriptionBarValue,
                    },
                    {
                      dataKey: 'respond',
                      label: getTierLabelKey(SubscriptionTier.Respond),
                      color: TIER_BAR_COLORS.respond,
                      minBarSize: SUBSCRIPTION_BREAKDOWN_MIN_BAR_SIZE_PX,
                      valueFormatter: formatSubscriptionBarValue,
                    },
                  ]}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
              alignItems: 'center',
              columnGap: 2,
              rowGap: { xs: 1, sm: 0 },
              mt: 0.5,
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                justifySelf: { xs: 'center', sm: 'auto' },
              }}
            >
              <GroupsScrollButton
                direction="left"
                disabled={!canScrollLeft}
                onClick={() => scrollGroupsBy(-1)}
              />
              <Typography
                sx={{
                  color: COLORS.SAFEGUARD_BLUE,
                  fontSize: AXIS_LABEL_FONT_SIZE,
                  fontWeight: 500,
                }}
              >
                {t(StringKeys.GROUPS)}
              </Typography>
              <GroupsScrollButton
                direction="right"
                disabled={!canScrollRight}
                onClick={() => scrollGroupsBy(1)}
              />
            </Box>
            <ChartColorLegend />
          </Box>
        </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

type OccupancyListProps = {
  rows: GroupSubscriptionCounts[];
};

const OccupancyList = ({ rows }: OccupancyListProps) => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="subscription-breakdown-list"
      sx={{
        width: '100%',
        maxHeight: CHART_HEIGHT,
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRadius: 2,
        backgroundColor: PLOT_BACKGROUND,
        px: { xs: 1.5, md: 3 },
      }}
    >
      <Box
        data-testid="subscription-breakdown-list-header"
        sx={{
          ...LIST_HEADER_STICKY_SX,
          ...LIST_GRID_SX,
          pt: { xs: 2, md: 2.5 },
          pb: { xs: 1.5, md: 2 },
        }}
      >
        <Typography
          sx={{
            color: COLORS.SAFEGUARD_BLUE,
            fontSize: { xs: 13, md: 16 },
            fontWeight: 500,
            minWidth: 0,
          }}
        >
          {t(StringKeys.GROUP)}
        </Typography>
        <ListHeaderChip
          variant="connect"
          label={getTierLabelKey(SubscriptionTier.Connect)}
        />
        <ListHeaderChip
          variant="ops"
          label={getTierLabelKey(SubscriptionTier.Ops)}
        />
        <ListHeaderChip
          variant="respond"
          label={getTierLabelKey(SubscriptionTier.Respond)}
        />
      </Box>
      <Box
        sx={{
          ...LIST_GRID_SX,
          rowGap: { xs: 1.5, md: 2.5 },
          pb: { xs: 2, md: 2.5 },
        }}
      >
        {rows.map((row) => (
          <OccupancyListRow key={row.groupName} row={row} />
        ))}
      </Box>
    </Box>
  );
};

type OccupancyListRowProps = {
  row: GroupSubscriptionCounts;
};

const OccupancyListRow = ({ row }: OccupancyListRowProps) => (
  <>
    <Typography
      title={row.groupName}
      sx={{
        color: COLORS.TEXT_HEADER_GRAY,
        fontSize: { xs: 13, md: 15 },
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {row.groupName}
    </Typography>
    <OccupancyCount value={row.connect} color={TIER_BAR_COLORS.connect} />
    <OccupancyCount value={row.ops} color={TIER_BAR_COLORS.ops} />
    <OccupancyCount value={row.respond} color={TIER_BAR_COLORS.respond} />
  </>
);

type OccupancyCountProps = {
  value: number;
  color: string;
};

const OccupancyCount = ({ value, color }: OccupancyCountProps) => (
  <Typography
    sx={{
      color,
      fontSize: { xs: 13, md: 15 },
      fontWeight: 500,
      textAlign: 'center',
      minWidth: 0,
    }}
  >
    {formatSubscriptionListCount(value)}
  </Typography>
);

type ListHeaderChipProps = {
  variant: 'connect' | 'ops' | 'respond';
  label: string;
};

const ListHeaderChip = ({ variant, label }: ListHeaderChipProps) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, width: '100%' }}>
    <Chip
      size="small"
      label={label}
      sx={{
        ...getTierChipSx(variant),
        width: '100%',
        maxWidth: '100%',
        fontSize: { xs: 11, md: 13 },
        fontWeight: 500,
        height: { xs: 24, md: 32 },
        py: 0,
        px: { xs: 0.25, md: 0.5 },
        borderRadius: 3,
        '& .MuiChip-label': {
          px: { xs: 0.25, md: 0.5 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  </Box>
);

const HUG_CHIP_SX = {
  width: 'fit-content',
  maxWidth: 'fit-content',
  fontSize: 13,
};

type PoolSummaryRowProps = {
  variant: 'ops' | 'respond';
  label: string;
  counts: SubscriptionPoolCounts;
};

const PoolSummaryRow = ({ variant, label, counts }: PoolSummaryRowProps) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        columnGap: { sm: 6 },
        rowGap: 0.75,
      }}
    >
      <Chip
        size="small"
        label={label}
        sx={{
          ...getTierChipSx(variant),
          ...HUG_CHIP_SX,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 0.5,
        }}
      >
        <PoolSummaryMetric value={counts.purchased} label={t(StringKeys.PURCHASED)} />
        <PoolSummaryMetric value={counts.used} label={t(StringKeys.USED)} />
        <PoolSummaryMetric value={counts.remaining} label={t(StringKeys.AVAILABLE)} />
      </Box>
    </Box>
  );
};

type PoolSummaryMetricProps = {
  value: number;
  label: string;
};

const PoolSummaryMetric = ({ value, label }: PoolSummaryMetricProps) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
    <Typography
      sx={{
        color: COLORS.SAFEGUARD_BLUE,
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ color: COLORS.GREY, fontSize: 13, fontWeight: 400 }}>
      {label}
    </Typography>
  </Box>
);

const ChartColorLegend = () => (
  <Box
    data-testid="subscription-breakdown-legend"
    sx={{
      width: '100%',
      display: 'flex',
      justifyContent: { xs: 'center', sm: 'flex-end' },
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      {CHART_LEGEND_ITEMS.map((item) => (
        <Box
          key={item.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: CHART_LEGEND_SWATCH_WIDTH_PX,
              height: CHART_LEGEND_SWATCH_HEIGHT_PX,
              borderRadius: 0.5,
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: item.color,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

type GroupsScrollButtonProps = {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
};

const GroupsScrollButton = ({
  direction,
  disabled,
  onClick,
}: GroupsScrollButtonProps) => {
  const { t } = useTranslation();
  return (
    <IconButton
      aria-label={
        direction === 'left'
          ? t(StringKeys.SCROLL_GROUPS_LEFT)
          : t(StringKeys.SCROLL_GROUPS_RIGHT)
      }
      disabled={disabled}
      onClick={onClick}
      size="small"
      sx={{
        width: GROUPS_SCROLL_BUTTON_SIZE_PX,
        height: GROUPS_SCROLL_BUTTON_SIZE_PX,
        border: `1px solid ${COLORS.LIGHT_GRAY}`,
        backgroundColor: COLORS.WHITE,
        color: COLORS.HOVER_GRAY,
        '&:hover': {
          backgroundColor: COLORS.TABLE_HEADER_GRAY,
          color: COLORS.CONNECT_TEXT_GRAY,
        },
        '&.Mui-disabled': {
          borderColor: COLORS.MODAL_HEADER_GRAY,
          backgroundColor: COLORS.LIGHTEST_GRAY,
          color: COLORS.LIGHT_GRAY,
          opacity: 0.5,
        },
      }}
    >
      {direction === 'left' ? (
        <ChevronLeftIcon fontSize="small" />
      ) : (
        <ChevronRightIcon fontSize="small" />
      )}
    </IconButton>
  );
};

export default SubscriptionBreakdownOverlay;
